import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import {
  IDispositivoAlarma,
  IFilter,
  ILuminaria,
  IPopulate,
  IQueryParam,
  IReporteBase,
  IReporteGenerico,
} from 'modelos/src';
import { IAlerta } from 'modelosSeguridad/src';
import { Estados } from 'modelosSeguridad/src/interfaces/alertas';
import { Subscription } from 'rxjs';
import { HelperService } from '../../../../auxiliares/servicios/helper.service';
import { ActivatedRoute } from '@angular/router';
import { Lista2Service } from '../../../../auxiliares/servicios/lista2.service';
import { MapaFacilV2Service } from '../../../../auxiliares/mapbox/mapa-facil-v2.service';
import { LuminariasLayerManager } from '../../../../auxiliares/mapbox/entity-managers/luminarias-layer.manager';
import { SirenaMapa } from '../../sirenas/mapa-sirenas/mapa-sirenas.component';
import { ISirenaExtendida } from '../../../../auxiliares/mapbox/entity-managers';
import { MatDialog } from '@angular/material/dialog';
import mapboxgl from 'mapbox-gl';
import { puntoValido } from '../../../../auxiliares/mapbox/ubicacion-valida';

interface ICapaMapaGeneral {
  clave: string;
  label: string;
  icono: string;
  colorCluster: string; // borde del botón de agrupar cuando está activo
  cantidad: number;
  visible: boolean;
  agrupada: boolean;
  alternar: () => void;
  alternarAgrupado: () => void;
}

@Component({
  selector: 'app-mapa-ol',
  templateUrl: './mapa-ol.component.html',
  styleUrl: './mapa-ol.component.scss',
  standalone: false,
})
export class MapaOlComponent implements OnInit, OnDestroy {
  private lista2Service = inject(Lista2Service);
  private helper = inject(HelperService);
  private activatedRoute = inject(ActivatedRoute);
  mapaFacilV2Service = inject(MapaFacilV2Service);
  private luminariasLayerManager = inject(LuminariasLayerManager);
  private dialog = inject(MatDialog);

  // Datos de reportes
  public reportesColectivos?: IReporteBase<'Tracker 4G'>[] = [];
  public reportesColectivos$?: Subscription;

  public reportesActivos?: IReporteBase<
    'Tracker 4G' | 'Tracker T1000B' | 'Tracker Qualcomm'
  >[] = [];
  public reportesActivos$?: Subscription;

  public reportesTrackers: IReporteBase<'Tracker 4G'>[] = [];
  public reportesTrackers$?: Subscription;

  public reportesVehiculos?: IReporteBase<'Tracker 4G'>[] = [];
  public reportesVehiculos$?: Subscription;

  public luminarias: ILuminaria[] = [];
  public luminarias$?: Subscription;

  public alarmas?: IDispositivoAlarma[] = [];
  public alarmas$?: Subscription;

  public alertasSeguridad?: IAlerta[] = [];
  public alertasSeguridad$?: Subscription;

  public sirenasG?: SirenaMapa[] = [];
  public sirenasG$?: Subscription;
  public sirenas$?: Subscription;

  private map: mapboxgl.Map | null = null;
  private dialogClose$?: Subscription;

  // Estados de visibilidad (igual que en legacy: true por defecto)
  public vehiculosHabilitados = true;
  public colectivosHabilitados = true;
  public trackersHabilitados = true;
  public activosHabilitados = true;
  public luminariasHabilitadas = true;
  public alarmasHabilitadas = true;
  public alertasSeguridadHabilitadas = true;
  public sirenasGHabilitadas = true;

  // Estados de clustering (false por defecto, se habilitan en iniciarMapaFacil)
  public vehiculosClusterEnabled = false;
  public colectivosClusterEnabled = false;
  public trackersClusterEnabled = false;
  public activosClusterEnabled = false;
  public luminariasClusterEnabled = false;
  public alarmasClusterEnabled = false;
  public alertasSeguridadClusterEnabled = false;
  public sirenasGClusterEnabled = false;

  public allowedTypes: string[] = [
    'vehiculos',
    'colectivos',
    'trackers',
    'activos',
    'luminarias',
    'alarmas',
    'alertasSeguridad',
    'sirenas',
  ];

  // Estados de alerta considerados "activos" — copiado de
  // alertas-seguridad/listado/alertas-seguridad.component.ts (fuente de verdad)
  private static readonly ESTADOS_ALERTA_ACTIVAS: Estados[] = [
    'Sin Tratamiento',
    'Pendiente',
    'En Proceso',
    'Liberada',
    'Móvil Enviado',
    'Móvil En Destino',
    'Derivada Externa',
    'Derivada Compartida',
  ];

  // Listados

  private async listarAlarmas(): Promise<void> {
    if (!this.helper.tieneModulo('Alarmas')) return;
    try {
      const populate: IPopulate[] = [
        {
          path: 'domicilio',
          select: 'geojson domicilio',
        },
      ];
      const query: IQueryParam = {
        select: 'idDomicilio',
        populate: JSON.stringify(populate),
        includeChildren: true,
        excludeTotalCount: true,
        unlimited: true,
      };

      this.alarmas$?.unsubscribe();
      this.alarmas$ = this.lista2Service
        .dispositivosAlarmas(query, 120)
        .subscribe(async (data) => {
          this.alarmas = data.datos;
          this.addMarkersAlarmas();
        });
    } catch (error) {
      this.helper.notifError('Error al listar alarmas');
    }
  }

  private async listarAlertasSeguridad(): Promise<void> {
    if (!this.helper.tieneModulo('Alertas de Seguridad')) return;
    try {
      const filter: IFilter<IAlerta> = {
        estadoActual: { $in: MapaOlComponent.ESTADOS_ALERTA_ACTIVAS },
      } as IFilter<IAlerta>;
      const query: IQueryParam = {
        filter: JSON.stringify(filter),
        select:
          '_id estadoActual fechaCreacion geojson ubicacion idCliente prioridad',
        includeChildren: true,
        excludeTotalCount: true,
      };

      this.alertasSeguridad$?.unsubscribe();
      this.alertasSeguridad$ = this.lista2Service
        .alertasSeguridad(query, 30)
        .subscribe(async (data) => {
          this.alertasSeguridad = data.datos;
          this.addMarkersAlertasSeguridad();
        });
    } catch (error) {
      this.helper.notifError('Error al listar alertas de seguridad');
    }
  }

  private async listarLuminarias(): Promise<void> {
    if (!this.helper.tieneModulo('Luminarias')) return;
    try {
      // Todas (sin tope: antes cortaba en 1000) y sólo lo que se dibuja; el
      // popup pide la luminaria entera al abrirse.
      const query: IQueryParam = {
        includeChildren: true,
        excludeTotalCount: true,
        unlimited: true,
        select:
          '_id identificacion deveui ubicacion estado direccion tipoDispositivo',
      };

      this.luminarias$?.unsubscribe();
      this.luminarias$ = this.lista2Service
        .luminarias(query, 30)
        .subscribe(async (data) => {
          this.luminarias = data.datos;
          this.addMarkersLuminarias();
        });
    } catch (error) {
      this.helper.notifError('Error al listar luminarias');
    }
  }

  private async listarReportesColectivos(): Promise<void> {
    if (!this.helper.tieneModulo('Colectivos')) return;
    try {
      const filter: IFilter<IReporteGenerico> = {
        tipoEntidad: 'Colectivo',
      };

      const query: IQueryParam = {
        filter: JSON.stringify(filter),
        select: 'valores.geojson',
        includeChildren: true,
        excludeTotalCount: true,
      };

      this.reportesColectivos$?.unsubscribe();
      this.reportesColectivos$ = this.lista2Service
        .ultimosReportesGenericos<'Tracker 4G'>(query, 30)
        .subscribe(async (data) => {
          this.reportesColectivos = data.datos;
          this.addMarkersColectivos();
        });
    } catch (error) {
      this.helper.notifError('Error al listar colectivos');
    }
  }

  private async listarReportesTrackers(): Promise<void> {
    if (!this.helper.tienePermiso('Administración - Ver trackers')) return;
    try {
      const filter: IFilter<IReporteGenerico> = {
        tipoEntidad: 'Tracker',
      };

      const query: IQueryParam = {
        filter: JSON.stringify(filter),
        select: 'valores.geojson',
        includeChildren: true,
        excludeTotalCount: true,
      };

      this.reportesTrackers$?.unsubscribe();
      this.reportesTrackers$ = this.lista2Service
        .ultimosReportesGenericos<'Tracker 4G'>(query, 30)
        .subscribe(async (data) => {
          this.reportesTrackers = data.datos;
          this.addMarkersTrackers();
        });
    } catch (error: any) {
      this.helper.notifError('Error al listar trackers');
    }
  }

  private async listarReportesActivos(): Promise<void> {
    if (!this.helper.tieneModulo('Activos')) return;
    try {
      const filter: IFilter<IReporteGenerico> = {
        tipoEntidad: 'Activo',
      };

      const query: IQueryParam = {
        filter: JSON.stringify(filter),
        select: 'valores.geojson',
        includeChildren: true,
        excludeTotalCount: true,
      };

      this.reportesActivos$?.unsubscribe();
      this.reportesActivos$ = this.lista2Service
        .ultimosReportesGenericos<
          'Tracker 4G' | 'Tracker T1000B' | 'Tracker Qualcomm'
        >(query, 30)
        .subscribe(async (data) => {
          this.reportesActivos = data.datos;
          this.addMarkersActivos();
        });
    } catch (error: any) {
      this.helper.notifError('Error al listar activos');
    }
  }

  private async listarReportesVehiculos(): Promise<void> {
    if (!this.helper.tieneModulo('Vehículos')) return;
    try {
      const filter: IFilter<IReporteGenerico> = {
        tipoEntidad: 'Vehiculo',
      };

      const query: IQueryParam = {
        filter: JSON.stringify(filter),
        select: 'valores.geojson',
        includeChildren: true,
        excludeTotalCount: true,
        unlimited: true,
      };

      this.reportesVehiculos$?.unsubscribe();
      this.reportesVehiculos$ = this.lista2Service
        .ultimosReportesGenericos<'Tracker 4G'>(query, 30)
        .subscribe(async (data) => {
          this.reportesVehiculos = data.datos;
          this.addMarkersVehiculos();
        });
    } catch (error: any) {
      this.helper.notifError('Error al listar vehiculos');
    }
  }

  private async listarSirenasG(): Promise<void> {
    if (!this.helper.tieneModulo('Sirenas')) return; // TODO-PERMISO: no hay AccionRol equivalente
    try {
      const query: IQueryParam = {
        includeChildren: true,
        excludeTotalCount: true,
        populate: JSON.stringify([
          { path: 'cliente', select: 'nombre' },
        ] as IPopulate[]),
        unlimited: true,
        sort: 'fechaCreacion',
        select: 'idExterno',
      };

      //Primero se listan las sirenas que están en gestión
      this.sirenasG$?.unsubscribe();
      this.sirenasG$ = this.lista2Service
        .sirenasG(query, 0)
        .subscribe(async (data) => {
          this.sirenasG = data.datos;
          // Después de cargar sirenasG, con el idExterno se cargan las sirenas asociadas pero que están en Seguridad
          await this.iniciarActualizacionSirenas();
        });
    } catch (error) {
      this.helper.notifError('Error al listar sirenasG');
    }
  }

  private async iniciarActualizacionSirenas(): Promise<void> {
    const query = {
      select: 'geojson idExterno',
      includeChildren: true,
    };

    //Acá traemos las sirenas G que están en seguridad, para optimizar, solo traemos la ubicación.
    this.sirenas$?.unsubscribe();
    this.sirenas$ = this.lista2Service
      .sirenas(query, 30)
      .subscribe((response) => {
        // Con los datos traídos de seguridad, actualizamos la sirenaG
        //Realmente se actualiza SirenaMapa, la cual es una extensión de sirenaG, pero que tiene un campo
        //llamado sirena en donde se guarda la info que se trae de seguridad.
        this.sirenasG?.forEach((sirenaG) => {
          sirenaG.sirena = response.datos.find(
            (s) => s._id === sirenaG.idExterno,
          );
        });
        // Actualizar marcadores en el mapa
        this.addMarkersSirenasG();
      });
  }

  // MÉTODOS PARA AGREGAR DATOS AL MAPA

  private addMarkersAlarmas() {
    if (!this.mapaFacilV2Service.isReady()) return;

    this.mapaFacilV2Service.updateData({
      alarmas: this.alarmas || [],
    });
  }

  private addMarkersAlertasSeguridad() {
    if (!this.mapaFacilV2Service.isReady()) return;

    this.mapaFacilV2Service.updateData({
      alertasSeguridad: this.alertasSeguridad || [],
    });
  }

  private addMarkersLuminarias() {
    if (!this.mapaFacilV2Service.isReady()) return;

    this.mapaFacilV2Service.updateData({
      luminarias: this.luminarias || [],
    });
  }

  private addMarkersSirenasG() {
    if (!this.mapaFacilV2Service.isReady()) return;

    //Transformar SirenaMapa a la estructura de sirena extendida que se usa en el mapa fácil.
    //Sólo vamos a agregar el id y las coordenadas, el resto de los datos se traerán al tocar el popup.
    const sirenas = (this.sirenasG || [])
      .filter((sg) => sg.sirena?.geojson?.coordinates)
      .map((sg) => {
        const sirena = sg.sirena!;
        const sirenaExtendida: ISirenaExtendida = {
          _id: sirena._id,
          coordenadas: [
            sirena.geojson!.coordinates[0],
            sirena.geojson!.coordinates[1],
          ] as [number, number],
        };
        return sirenaExtendida;
      });

    this.mapaFacilV2Service.updateData({ sirenas: sirenas });
  }
  private addMarkersColectivos() {
    if (!this.mapaFacilV2Service.isReady()) return;

    this.mapaFacilV2Service.updateData({
      colectivos: this.reportesColectivos || [],
    });
  }

  private addMarkersVehiculos() {
    if (!this.mapaFacilV2Service.isReady()) return;

    this.mapaFacilV2Service.updateData({
      vehiculos: this.reportesVehiculos || [],
    });
  }

  private addMarkersTrackers() {
    if (!this.mapaFacilV2Service.isReady()) return;

    this.mapaFacilV2Service.updateData({
      trackers: this.reportesTrackers || [],
    });
  }

  private addMarkersActivos() {
    if (!this.mapaFacilV2Service.isReady()) return;

    this.mapaFacilV2Service.updateData({
      activos: this.reportesActivos || [],
    });
  }

  private async listarTodo() {
    // Listar todos los datos
    await Promise.all([
      this.listarReportesTrackers(),
      this.listarReportesColectivos(),
      this.listarReportesActivos(),
      this.listarReportesVehiculos(),
      this.listarAlarmas(),
      this.listarAlertasSeguridad(),
      this.listarLuminarias(),
      this.listarSirenasG(),
    ]);
  }

  public onMapReady(map: mapboxgl.Map) {
    this.map = map;
    // Reset modo del singleton: mapa general usa cluster amarillo unificado,
    // no donut multicolor (que es exclusivo de mapa-luminarias).
    this.luminariasLayerManager.setMapaEspecifico(false);
    this.dialogClose$ = this.dialog.afterAllClosed.subscribe(() => {
      if (this.map) {
        this.mapaFacilV2Service.restore(this.map);
        this.restaurarEstadoMapa();
      }
    });
    this.iniciarMapaFacil();
  }

  private restaurarEstadoMapa() {
    if (!this.mapaFacilV2Service.isReady()) return;

    // Re-aplicar clustering con el estado actual (sin resetear)
    this.mapaFacilV2Service.setClusteringEnabled(
      'vehiculos',
      this.vehiculosClusterEnabled,
    );
    this.mapaFacilV2Service.setClusteringEnabled(
      'colectivos',
      this.colectivosClusterEnabled,
    );
    this.mapaFacilV2Service.setClusteringEnabled(
      'trackers',
      this.trackersClusterEnabled,
    );
    this.mapaFacilV2Service.setClusteringEnabled(
      'activos',
      this.activosClusterEnabled,
    );
    this.mapaFacilV2Service.setClusteringEnabled(
      'luminarias',
      this.luminariasClusterEnabled,
    );
    this.mapaFacilV2Service.setClusteringEnabled(
      'alarmas',
      this.alarmasClusterEnabled,
    );
    this.mapaFacilV2Service.setClusteringEnabled(
      'alertasSeguridad',
      this.alertasSeguridadClusterEnabled,
    );
    this.mapaFacilV2Service.setClusteringEnabled(
      'sirenas',
      this.sirenasGClusterEnabled,
    );

    // Re-aplicar visibilidad con el estado actual
    this.mapaFacilV2Service.setEntityVisibility(
      'vehiculos',
      this.vehiculosHabilitados,
    );
    this.mapaFacilV2Service.setEntityVisibility(
      'colectivos',
      this.colectivosHabilitados,
    );
    this.mapaFacilV2Service.setEntityVisibility(
      'trackers',
      this.trackersHabilitados,
    );
    this.mapaFacilV2Service.setEntityVisibility(
      'activos',
      this.activosHabilitados,
    );
    this.mapaFacilV2Service.setEntityVisibility(
      'luminarias',
      this.luminariasHabilitadas,
    );
    this.mapaFacilV2Service.setEntityVisibility(
      'alarmas',
      this.alarmasHabilitadas,
    );
    this.mapaFacilV2Service.setEntityVisibility(
      'alertasSeguridad',
      this.alertasSeguridadHabilitadas,
    );
    this.mapaFacilV2Service.setEntityVisibility(
      'sirenas',
      this.sirenasGHabilitadas,
    );

    // Re-aplicar datos
    this.addMarkersAlarmas();
    this.addMarkersAlertasSeguridad();
    this.addMarkersLuminarias();
    this.addMarkersSirenasG();
    this.addMarkersColectivos();
    this.addMarkersVehiculos();
    this.addMarkersActivos();
    this.addMarkersTrackers();
  }

  public iniciarMapaFacil() {
    if (this.mapaFacilV2Service.isReady()) {
      // Habilitar clustering
      this.mapaFacilV2Service.setClusteringEnabled('alarmas', true);
      this.mapaFacilV2Service.setClusteringEnabled('alertasSeguridad', true);
      this.mapaFacilV2Service.setClusteringEnabled('luminarias', true);
      this.mapaFacilV2Service.setClusteringEnabled('colectivos', true);
      this.mapaFacilV2Service.setClusteringEnabled('vehiculos', true);
      this.mapaFacilV2Service.setClusteringEnabled('activos', true);
      this.mapaFacilV2Service.setClusteringEnabled('trackers', true);
      this.mapaFacilV2Service.setClusteringEnabled('sirenas', true);

      // Actualizar estados locales de clustering para los botones
      this.alarmasClusterEnabled = true;
      this.alertasSeguridadClusterEnabled = true;
      this.luminariasClusterEnabled = true;
      this.sirenasGClusterEnabled = true;
      this.colectivosClusterEnabled = true;
      this.vehiculosClusterEnabled = true;
      this.activosClusterEnabled = true;
      this.trackersClusterEnabled = true;

      // IMPORTANTE: Volver a cargar todos los datos que ya están en memoria
      // Los datos pueden haber llegado antes de que el mapa estuviera listo
      this.addMarkersAlarmas();
      this.addMarkersAlertasSeguridad();
      this.addMarkersLuminarias();
      this.addMarkersSirenasG();
      this.addMarkersColectivos();
      this.addMarkersVehiculos();
      this.addMarkersActivos();
      this.addMarkersTrackers();
    } else {
    }
  }

  async ngOnInit() {
    this.activatedRoute.queryParams.subscribe(async () => {
      await this.listarTodo();
    });
  }

  ngOnDestroy() {
    this.reportesTrackers$?.unsubscribe();
    this.reportesColectivos$?.unsubscribe();
    this.reportesActivos$?.unsubscribe();
    this.reportesVehiculos$?.unsubscribe();
    this.alarmas$?.unsubscribe();
    this.alertasSeguridad$?.unsubscribe();
    this.luminarias$?.unsubscribe();
    this.sirenasG$?.unsubscribe();
    this.sirenas$?.unsubscribe();
    this.dialogClose$?.unsubscribe();

    // IMPORTANTE: Limpiar datos del servicio para evitar que persistan al cambiar de mapa
    // El servicio es singleton, por lo que mantiene datos entre componentes
    this.mapaFacilV2Service.clearAllData();
  }

  // ========== TOGGLE DE VISIBILIDAD ==========

  /**
   * Botones de capas sobre el mapa: uno por tipo de entidad con datos.
   * `visible` y `agrupada` leen el estado de cada tipo; las acciones
   * reutilizan los toggles existentes.
   */
  get capas(): ICapaMapaGeneral[] {
    const capas: ICapaMapaGeneral[] = [
      {
        clave: 'vehiculos',
        colorCluster: '#2196F3',
        label: 'Vehículos',
        icono: 'directions_car',
        cantidad: this.reportesVehiculos?.length ?? 0,
        visible: this.vehiculosHabilitados,
        agrupada: this.vehiculosClusterEnabled,
        alternar: () => this.toggleVehiculos(),
        alternarAgrupado: () => this.toggleVehiculosCluster(),
      },
      {
        clave: 'colectivos',
        colorCluster: '#ff7300',
        label: 'Colectivos',
        icono: 'directions_bus',
        cantidad: this.reportesColectivos?.length ?? 0,
        visible: this.colectivosHabilitados,
        agrupada: this.colectivosClusterEnabled,
        alternar: () => this.toggleColectivos(),
        alternarAgrupado: () => this.toggleColectivosCluster(),
      },
      {
        clave: 'trackers',
        colorCluster: '#9C27B0',
        label: 'Trackers',
        icono: 'gps_fixed',
        cantidad: this.reportesTrackers?.length ?? 0,
        visible: this.trackersHabilitados,
        agrupada: this.trackersClusterEnabled,
        alternar: () => this.toggleTrackers(),
        alternarAgrupado: () => this.toggleTrackersCluster(),
      },
      {
        clave: 'activos',
        colorCluster: '#4CAF50',
        label: 'Activos',
        icono: 'assistant_navigation',
        cantidad: this.reportesActivos?.length ?? 0,
        visible: this.activosHabilitados,
        agrupada: this.activosClusterEnabled,
        alternar: () => this.toggleActivos(),
        alternarAgrupado: () => this.toggleActivosCluster(),
      },
      {
        clave: 'luminarias',
        colorCluster: '#FFC107',
        label: 'Luminarias',
        icono: 'lightbulb',
        // Las que se dibujan: con una ubicación real.
        cantidad: (this.luminarias ?? []).filter((l) =>
          puntoValido(l.ubicacion),
        ).length,
        visible: this.luminariasHabilitadas,
        agrupada: this.luminariasClusterEnabled,
        alternar: () => this.toggleLuminarias(),
        alternarAgrupado: () => this.toggleLuminariasCluster(),
      },
      {
        clave: 'alarmas',
        colorCluster: '#F44336',
        label: 'Alarmas',
        icono: 'doorbell',
        cantidad: this.alarmas?.length ?? 0,
        visible: this.alarmasHabilitadas,
        agrupada: this.alarmasClusterEnabled,
        alternar: () => this.toggleAlarmas(),
        alternarAgrupado: () => this.toggleAlarmasCluster(),
      },
      {
        clave: 'alertasSeguridad',
        colorCluster: '#E53935',
        label: 'Alertas de seguridad',
        icono: 'crisis_alert',
        cantidad: this.alertasSeguridad?.length ?? 0,
        visible: this.alertasSeguridadHabilitadas,
        agrupada: this.alertasSeguridadClusterEnabled,
        alternar: () => this.toggleAlertasSeguridad(),
        alternarAgrupado: () => this.toggleAlertasSeguridadCluster(),
      },
      {
        clave: 'sirenas',
        colorCluster: '#d898b2',
        label: 'Sirenas',
        icono: 'campaign',
        cantidad: this.sirenasG?.length ?? 0,
        visible: this.sirenasGHabilitadas,
        agrupada: this.sirenasGClusterEnabled,
        alternar: () => this.toggleSirenas(),
        alternarAgrupado: () => this.toggleSirenasGCluster(),
      },
    ];
    return capas.filter((c) => c.cantidad > 0);
  }

  /** Completo hasta 9.999; después "24 mil" y "1,1 M". */
  public formatoCantidad(n: number): string {
    if (n < 10_000) return n.toLocaleString('es-AR');
    if (n < 1_000_000) return Math.round(n / 1000) + ' mil';
    return (
      (n / 1_000_000).toLocaleString('es-AR', { maximumFractionDigits: 1 }) +
      ' M'
    );
  }

  toggleVehiculos() {
    this.vehiculosHabilitados = !this.vehiculosHabilitados;
    this.mapaFacilV2Service.setEntityVisibility(
      'vehiculos',
      this.vehiculosHabilitados,
    );
  }

  toggleColectivos() {
    this.colectivosHabilitados = !this.colectivosHabilitados;
    this.mapaFacilV2Service.setEntityVisibility(
      'colectivos',
      this.colectivosHabilitados,
    );
  }

  toggleTrackers() {
    this.trackersHabilitados = !this.trackersHabilitados;
    this.mapaFacilV2Service.setEntityVisibility(
      'trackers',
      this.trackersHabilitados,
    );
  }

  toggleActivos() {
    this.activosHabilitados = !this.activosHabilitados;
    this.mapaFacilV2Service.setEntityVisibility(
      'activos',
      this.activosHabilitados,
    );
  }

  toggleLuminarias() {
    this.luminariasHabilitadas = !this.luminariasHabilitadas;
    this.mapaFacilV2Service.setEntityVisibility(
      'luminarias',
      this.luminariasHabilitadas,
    );
  }

  toggleAlarmas() {
    this.alarmasHabilitadas = !this.alarmasHabilitadas;
    this.mapaFacilV2Service.setEntityVisibility(
      'alarmas',
      this.alarmasHabilitadas,
    );
  }

  toggleAlertasSeguridad() {
    this.alertasSeguridadHabilitadas = !this.alertasSeguridadHabilitadas;
    this.mapaFacilV2Service.setEntityVisibility(
      'alertasSeguridad',
      this.alertasSeguridadHabilitadas,
    );
  }

  toggleSirenas() {
    this.sirenasGHabilitadas = !this.sirenasGHabilitadas;
    this.mapaFacilV2Service.setEntityVisibility(
      'sirenas',
      this.sirenasGHabilitadas,
    );
  }

  // ========== TOGGLE DE CLUSTERING ==========

  toggleVehiculosCluster() {
    this.vehiculosClusterEnabled = !this.vehiculosClusterEnabled;
    this.mapaFacilV2Service.setClusteringEnabled(
      'vehiculos',
      this.vehiculosClusterEnabled,
    );
  }

  toggleColectivosCluster() {
    this.colectivosClusterEnabled = !this.colectivosClusterEnabled;
    this.mapaFacilV2Service.setClusteringEnabled(
      'colectivos',
      this.colectivosClusterEnabled,
    );
  }

  toggleTrackersCluster() {
    this.trackersClusterEnabled = !this.trackersClusterEnabled;
    this.mapaFacilV2Service.setClusteringEnabled(
      'trackers',
      this.trackersClusterEnabled,
    );
  }

  toggleActivosCluster() {
    this.activosClusterEnabled = !this.activosClusterEnabled;
    this.mapaFacilV2Service.setClusteringEnabled(
      'activos',
      this.activosClusterEnabled,
    );
  }

  toggleLuminariasCluster() {
    this.luminariasClusterEnabled = !this.luminariasClusterEnabled;
    this.mapaFacilV2Service.setClusteringEnabled(
      'luminarias',
      this.luminariasClusterEnabled,
    );
  }

  toggleAlarmasCluster() {
    this.alarmasClusterEnabled = !this.alarmasClusterEnabled;
    this.mapaFacilV2Service.setClusteringEnabled(
      'alarmas',
      this.alarmasClusterEnabled,
    );
  }

  toggleAlertasSeguridadCluster() {
    this.alertasSeguridadClusterEnabled = !this.alertasSeguridadClusterEnabled;
    this.mapaFacilV2Service.setClusteringEnabled(
      'alertasSeguridad',
      this.alertasSeguridadClusterEnabled,
    );
  }

  toggleSirenasGCluster() {
    this.sirenasGClusterEnabled = !this.sirenasGClusterEnabled;
    this.mapaFacilV2Service.setClusteringEnabled(
      'sirenas',
      this.sirenasGClusterEnabled,
    );
  }
}
